import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { JournalEntryMatchAction, PaymentRecordType } from '../../../../enums/payables/payable-action.enum';
import { JournalEntryLineType } from '../../../../enums/journal-entry-line-type.enum';
import { Transaction } from '../../../../models/entities/transactions/transaction';
import { JournalEntry } from '../../../../models/entities/transactions/journal-entries/journal-entry';
import { Liability } from '../../../../models/entities/accounting/liability';
import { FixedAsset } from '../../../../models/entities/accounting/fixed-asset';
import { Organization } from '../../../../models/entities/organization';
import { Payable } from '../../../../models/entities/payable';
import { CommonModule } from '@angular/common';
import { BankAccountSelectorComponent } from '../../shared/bank-account-selector/bank-account-selector.component';
import { ConfirmDialogComponent } from '../../../v2-layout/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { TransactionsService } from '../../../../services/accounting/transactions.service';
import { ChartOfAccountsService } from '../../../../services/accounting/chart-of-accounts.service';
import { OrganizationService } from '../../../../services/organization/organization.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { LegfiDatePipe } from 'app/pipes/format/legfi-date.pipe';
import { LegfiCurrencyPipe } from 'app/pipes/format/legfi-currency.pipe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import moment from 'moment-timezone';

interface RecordTypeItem {
    value: PaymentRecordType;
    label: string;
    detail?: string;
    disabled?: boolean;
}

interface TransactionItem {
    value: number;
    label: string;
    badge?: string;
    badgeColor?: string;
}

interface JournalEntryActionItem {
    value: JournalEntryMatchAction;
    label: string;
    detail: string;
}

/** One debit or credit line of a ledger entry, in cents. */
interface LedgerLine {
    account: string;
    debit: number;
    credit: number;
}

/** A ledger line as the JE panel's tables show it: amounts formatted, an empty side as a dash. */
export interface LedgerRowView {
    id: number;
    account: string;
    debit: string;
    credit: string;
}

/** What the JE panel needs to know about the org's books to describe a REPLACE truthfully. */
interface AccountingContext {
    useCashAccounting: boolean;
    accountsPayableLabel: string;
}

interface JournalEntryMatchState {
    transaction: Transaction;
    context: AccountingContext;
}

const JOURNAL_ENTRY_BADGE = 'Journal entry';
const JOURNAL_ENTRY_BADGE_COLOR = 'blue';
const DEFAULT_ACCOUNTS_PAYABLE_LABEL = 'Accounts Payable';
const EMPTY_AMOUNT = '—';
const CASH_REPLACE_FOOTNOTE = 'Standard entry for cash-basis accounting.';
const ACCRUAL_REPLACE_FOOTNOTE = 'Standard entry for accrual-basis accounting.';
/** How core types a fixed asset that is a bank account's ledger account. */
const UNIFIED_BANK_ACCOUNT_OWNER_TYPE = 'App\\Models\\Entities\\UnifiedBankAccount';

/** Shown by the host dialogs when submit is pressed before a matched journal entry's lines arrive. */
export const JOURNAL_ENTRY_LOADING_MESSAGE = 'The journal entry is still loading. Try again in a moment.';

@UntilDestroy()
@Component({
    selector: 'app-mark-as-paid-form',
    templateUrl: './mark-as-paid-form.component.html',
    styleUrls: ['./mark-as-paid-form.component.scss'],
    standalone: true,
    imports: [CommonModule,
        ReactiveFormsModule,
        BankAccountSelectorComponent,
        ConfirmDialogComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MarkAsPaidFormComponent implements OnInit, OnChanges {
    @Input() localPayable: Payable;
    @Input() potentialTransactionMatches: Transaction[] = [];
    /**
     * The review card's "Journal entry" line, pushed to the host rather than read through a
     * ViewChild: the host's binding would otherwise change after it was checked. Async so an
     * emission made while this form initialises lands in the host's next change detection.
     */
    @Output() journalEntrySummaryChange = new EventEmitter<string | null>(true);

    markAsPaidFormGroup: UntypedFormGroup;

    readonly PaymentRecordType = PaymentRecordType;
    readonly JournalEntryMatchAction = JournalEntryMatchAction;
    readonly CREATE_PAYMENT_FIELDS = ['bankAccountForTransaction', 'paymentDate', 'paymentMemo'] as const;
    readonly REQUIRED_FIELDS_FOR_CREATE = ['bankAccountForTransaction', 'paymentDate'] as const;

    recordTypeItems: RecordTypeItem[] = [];
    transactionItems: TransactionItem[] = [];
    journalEntryActionItems: JournalEntryActionItem[] = [];

    /** The matched journal entry, loaded with its lines; null when the match isn't a journal entry. */
    matchedJournalEntry: Transaction = null;
    isLoadingJournalEntry = false;
    accountingContext: AccountingContext = null;

    // Display state for the template, recomputed by refreshJournalEntryView() whenever the form or
    // the loaded entry changes, so the template reads fields rather than calling getters every tick.
    journalEntryAction: JournalEntryMatchAction | null = null;
    isJournalEntryMatch = false;
    /** The picked transaction is a journal entry, known before its lines load; drives the chip. */
    isJournalEntrySelected = false;
    journalEntryName = '';
    replaceFootnote = CASH_REPLACE_FOOTNOTE;

    currentEntryRows: LedgerRowView[] = [];
    currentEntryFooter: LedgerRowView = undefined;
    /**
     * The bank a REPLACE starts on: the one bank account the journal entry already credits, so the
     * user usually only confirms it. Null when it credits none (or several) — the user picks.
     */
    suggestedReplaceBankAccountId: number = null;

    accrualKeepConfirmOpen = false;
    readonly accrualKeepConfirmMessage = 'This journal entry doesn\'t record a payment against Accounts Payable, '
            + 'so this invoice will remain in Accounts Payable after it\'s marked paid. '
            + 'Would you like to keep the existing journal entry anyway?';
    private _pendingSubmission: () => void = null;

    /** Emits the journal entry to load, or null to cancel an in-flight load when the match moves off it. */
    private readonly _matchedJournalEntryId$ = new Subject<number | null>();
    private _standardEntryView: { key: string, rows: LedgerRowView[], footer: LedgerRowView } = null;
    private _emittedJournalEntrySummary: string | null | undefined = undefined;
    private _accountingContext$: Observable<AccountingContext> = null;

    constructor(
            private _formBuilder: UntypedFormBuilder,
            private _legfiDatePipe: LegfiDatePipe,
            private _legfiCurrencyPipe: LegfiCurrencyPipe,
            private _transactionsService: TransactionsService,
            private _organizationService: OrganizationService,
            private _chartOfAccountsService: ChartOfAccountsService,
            private _growlerService: GrowlerService,
    ) { }

    ngOnInit(): void {
        this.markAsPaidFormGroup = this.initMarkAsPaidForm();
        this.setupPaymentRecordTypeValidation();
        this.setupJournalEntryMatching();
        this.handleTransactionMatchingState(this.potentialTransactionMatches);
        this.refreshJournalEntryView();
        this.emitJournalEntrySummary();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.potentialTransactionMatches && this.markAsPaidFormGroup) {
            this.handleTransactionMatchingState(this.potentialTransactionMatches || []);
        }
    }

    handleTransactionMatchingState(matches: Transaction[]): void {
        const paymentRecordTypeControl = this.markAsPaidFormGroup.get('paymentRecordType');
        const matchControl = this.markAsPaidFormGroup.get('existingTransactionToMatch');

        this.recordTypeItems = [
            {
                value: PaymentRecordType.MATCH,
                label: 'Match with an existing transaction',
                detail: matches.length === 0 ? this.noMatchReason() : undefined,
                disabled: matches.length === 0,
            },
            {
                value: PaymentRecordType.CREATE,
                label: 'Create a new payment record',
            },
        ];
        // SD-9003: the design's "Journal entry" chip after the label tells the user which matches
        // carry the keep/replace choice before one is picked. Order is left as the server sent it.
        this.transactionItems = matches.map(transaction => ({
            value: transaction.id,
            label: `${this._legfiDatePipe.transform(transaction.transactionDate, 'MM/DD/YYYY')} • ${transaction.description} • ${this._legfiCurrencyPipe.transform(transaction.amount, 'USD', 'symbol', '.2')}`,
            ...(transaction.journalEntry ? {badge: JOURNAL_ENTRY_BADGE, badgeColor: JOURNAL_ENTRY_BADGE_COLOR} : {}),
        }));

        if (matches.length > 0) {
            matchControl.enable();
            // The list reloads when the payable changes (e.g. its amount, via Back): a pick that is no
            // longer offered must go, or its journal entry would still be shown and sent.
            const pickedId = matchControl.value;
            if (pickedId !== null && pickedId !== undefined && !matches.some(match => match.id === Number(pickedId))) {
                matchControl.setValue(null);
            }
            paymentRecordTypeControl.setValue(PaymentRecordType.MATCH);
        } else {
            matchControl.disable();
            matchControl.setValue(null);
            paymentRecordTypeControl.setValue(PaymentRecordType.CREATE);
        }
        this.refreshJournalEntryView();
    }

    selectRecordType(type: PaymentRecordType): void {
        this.markAsPaidFormGroup.get('paymentRecordType').setValue(type);
    }

    /**
     * The review card's "Journal entry" line: which entry will represent this payment in the ledger.
     * Null when nothing has been chosen yet, so the review can leave the line out.
     */
    get journalEntrySummary(): string | null {
        const recordType = this.markAsPaidFormGroup?.get('paymentRecordType').value;
        if (recordType === PaymentRecordType.CREATE) {
            return 'Standard payment entry';
        }

        const match = this.selectedMatch();

        // Read off the picked match, not the loaded entry, so the line is right while the lines load.
        if (match?.journalEntry) {
            return this.currentJournalEntryAction() === JournalEntryMatchAction.REPLACE ? 'Standard payment entry' : `Journal Entry #${match.id}`;
        }

        return match?.description ?? null;
    }

    selectJournalEntryAction(action: JournalEntryMatchAction): void {
        this.markAsPaidFormGroup.get('journalEntryAction').setValue(action);
    }

    /** Mirrors the server's match rule: same amount, unlinked, dated after the invoice date less two months. */
    private noMatchReason(): string {
        const amount = this._legfiCurrencyPipe.transform(this.localPayable?.amount, 'USD', 'symbol', '.2');
        const since = moment(this.localPayable?.invoiceDate ?? undefined).subtract(2, 'months');

        return `No unlinked ${amount} transaction dated after ${since.format('MM/DD/YYYY')}`;
    }

    isValid(): boolean {
        this.markAsPaidFormGroup.markAllAsTouched();
        // A journal-entry match can't be submitted until its lines have loaded: the user has to see
        // what they are keeping or replacing before the choice is sent.
        return this.markAsPaidFormGroup.valid && !this.isLoadingJournalEntry;
    }

    /**
     * Runs the host's submission, first asking for confirmation when an accrual org keeps a journal
     * entry that never debits Accounts Payable — that invoice would otherwise sit in A/P after it's
     * marked paid with nothing on screen saying so.
     */
    confirmSubmission(submit: () => void): void {
        if (!this.requiresAccrualKeepConfirmation()) {
            submit();
            return;
        }

        this._pendingSubmission = submit;
        this.accrualKeepConfirmOpen = true;
    }

    onAccrualKeepConfirmClosed(confirmed: boolean): void {
        this.accrualKeepConfirmOpen = false;
        const submit = this._pendingSubmission;
        this._pendingSubmission = null;
        if (confirmed && submit) {
            submit();
        }
    }

    requiresAccrualKeepConfirmation(): boolean {
        if (!this.hasJournalEntryMatch() || this.currentJournalEntryAction() !== JournalEntryMatchAction.KEEP) {
            return false;
        }
        if (this.accountingContext?.useCashAccounting !== false) {
            return false;
        }

        return !(this.matchedJournalEntry.journalEntries ?? []).some(line => this.isAccountsPayableDebit(line));
    }

    getPaymentDate(): string | null {
        return this.markAsPaidFormGroup.get('paymentDate').value;
    }

    getPaymentBankAccountId(): number | null {
        return this.markAsPaidFormGroup.get('bankAccountForTransaction').value?.id || null;
    }

    getFormGroup(): UntypedFormGroup {
        return this.markAsPaidFormGroup;
    }

    markAsUntouched(): void {
        this.markAsPaidFormGroup.markAsUntouched();
    }

    private initMarkAsPaidForm(): UntypedFormGroup {
        return this._formBuilder.group({
            paymentRecordType: this._formBuilder.control(PaymentRecordType.CREATE, {
                nonNullable: true
            }),
            existingTransactionToMatch: this._formBuilder.control({
                value: null,
                disabled: true
            }),
            // SD-9003: sent as markAsPaid.journalEntryAction. Core rejects any value unless the
            // matched transaction is a journal entry, so it stays null for every other path.
            journalEntryAction: this._formBuilder.control(null),
            bankAccountForTransaction: this._formBuilder.control(null),
            paymentDate: this._formBuilder.control(null),
            paymentMemo: this._formBuilder.control(null)
        });
    }

    private setupPaymentRecordTypeValidation(): void {
        this.markAsPaidFormGroup.get('paymentRecordType').valueChanges.pipe(untilDestroyed(this)).subscribe(recordType => {
            if (recordType === PaymentRecordType.CREATE) {
                this.setupCreatePaymentValidation();
            } else if (recordType === PaymentRecordType.MATCH) {
                this.setupMatchPaymentValidation();
            }
            this.markAsPaidFormGroup.updateValueAndValidity();
        });
    }

    private setupCreatePaymentValidation(): void {
        this.REQUIRED_FIELDS_FOR_CREATE.forEach(field => {
            const control = this.markAsPaidFormGroup.get(field);
            control.setValidators([Validators.required]);
            control.updateValueAndValidity();
        });

        const matchControl = this.markAsPaidFormGroup.get('existingTransactionToMatch');
        matchControl.clearValidators();
        matchControl.disable();
        matchControl.setValue(null);
    }

    private setupMatchPaymentValidation(): void {
        const matchControl = this.markAsPaidFormGroup.get('existingTransactionToMatch');
        matchControl.setValidators([Validators.required]);
        matchControl.enable();
        matchControl.updateValueAndValidity();

        this.CREATE_PAYMENT_FIELDS.forEach(field => {
            const control = this.markAsPaidFormGroup.get(field);
            control.clearValidators();
            control.setValue(null);
            control.updateValueAndValidity();
        });

        // The matches can reload under a chosen REPLACE (Back and forward again), which re-enters
        // this path: put the bank requirement back so REPLACE can't be sent without one.
        this.applyJournalEntryActionValidation(this.currentJournalEntryAction());
    }

    private setupJournalEntryMatching(): void {
        // enable()/updateValueAndValidity() re-emit the current pick whenever the matches reload;
        // only a real change of transaction may reset the keep/replace choice or refetch the entry.
        this.markAsPaidFormGroup.get('existingTransactionToMatch').valueChanges
                .pipe(
                        map((transactionId: number | string | null) => transactionId === null ? null : Number(transactionId)),
                        distinctUntilChanged(),
                        untilDestroyed(this),
                )
                .subscribe(transactionId => this.onMatchedTransactionChanged(transactionId));

        this._matchedJournalEntryId$
                .pipe(
                        // switchMap drops a superseded load, so a slow response for an earlier pick
                        // can never land on the current one; null cancels without loading anything.
                        switchMap(transactionId => transactionId === null ? EMPTY : this.loadJournalEntryMatch(transactionId)),
                        untilDestroyed(this),
                )
                .subscribe((state: JournalEntryMatchState) => {
                    this.isLoadingJournalEntry = false;
                    this.matchedJournalEntry = state.transaction;
                    this.accountingContext = state.context;
                    this.buildJournalEntryPreview();
                    this.refreshJournalEntryView();
                    this.emitJournalEntrySummary();
                });

        this.markAsPaidFormGroup.get('journalEntryAction').valueChanges
                .pipe(untilDestroyed(this))
                .subscribe(action => this.applyJournalEntryActionValidation(action));

        this.markAsPaidFormGroup.valueChanges
                .pipe(untilDestroyed(this))
                .subscribe(() => {
                    this.refreshJournalEntryView();
                    this.emitJournalEntrySummary();
                });
    }

    private refreshJournalEntryView(): void {
        const isMatchRecord = this.markAsPaidFormGroup.get('paymentRecordType').value === PaymentRecordType.MATCH;

        this.journalEntryAction = this.currentJournalEntryAction();
        this.isJournalEntryMatch = this.hasJournalEntryMatch();
        this.isJournalEntrySelected = isMatchRecord && !!this.selectedMatch()?.journalEntry;
        this.journalEntryName = this.matchedJournalEntry ? `Journal Entry #${this.matchedJournalEntry.id}` : '';
        this.replaceFootnote = this.accountingContext?.useCashAccounting === false ? ACCRUAL_REPLACE_FOOTNOTE : CASH_REPLACE_FOOTNOTE;
    }

    private currentJournalEntryAction(): JournalEntryMatchAction | null {
        return this.markAsPaidFormGroup.get('journalEntryAction').value ?? null;
    }

    private hasJournalEntryMatch(): boolean {
        return this.markAsPaidFormGroup.get('paymentRecordType').value === PaymentRecordType.MATCH
                && this.matchedJournalEntry !== null;
    }

    /** Tells the host the review line only when it actually changes. */
    private emitJournalEntrySummary(): void {
        const summary = this.journalEntrySummary;
        if (summary === this._emittedJournalEntrySummary) {
            return;
        }

        this._emittedJournalEntrySummary = summary;
        this.journalEntrySummaryChange.emit(summary);
    }

    private onMatchedTransactionChanged(transactionId: number | null): void {
        const match = this.potentialTransactionMatches.find(transaction => transaction.id === transactionId);

        if (!match?.journalEntry) {
            this._matchedJournalEntryId$.next(null);
            this.clearJournalEntryMatch();
            return;
        }

        // KEEP is the default: it posts nothing, so a user who never looks at the choice can't
        // destroy their own entry. It is also what core assumes when no choice arrives.
        this.matchedJournalEntry = null;
        this.journalEntryActionItems = [];
        this.isLoadingJournalEntry = true;
        this.markAsPaidFormGroup.get('journalEntryAction').setValue(JournalEntryMatchAction.KEEP);
        this._matchedJournalEntryId$.next(match.id);
    }

    private loadJournalEntryMatch(transactionId: number): Observable<JournalEntryMatchState> {
        const organizationId = this.organizationId();

        // The match list carries no lines; the single-transaction read loads journalEntries.class,
        // which is what names the real accounts in the preview.
        return forkJoin({
            transaction: this._transactionsService.getTransaction(organizationId, transactionId),
            context: this.loadAccountingContext(organizationId),
        }).pipe(
                // Caught here, not in the outer subscribe: an error there would end the stream and
                // leave every later journal-entry pick stuck loading.
                catchError(() => {
                    // Drop the pick itself, not only the panel: a pick left in place would submit
                    // with no keep/replace choice, and re-picking it would be swallowed as unchanged.
                    this.markAsPaidFormGroup.get('existingTransactionToMatch').setValue(null);
                    this.clearJournalEntryMatch();
                    this._growlerService.oops('There was a problem loading the journal entry.');

                    return EMPTY;
                }),
        );
    }

    /** Loaded once per form: the org's basis, and on accrual the name of its Accounts Payable account. */
    private loadAccountingContext(organizationId: number): Observable<AccountingContext> {
        if (!this._accountingContext$) {
            this._accountingContext$ = this._organizationService.getOrganizationById(organizationId, ['preferences']).pipe(
                    switchMap((organization: Organization) => {
                        const useCashAccounting = organization.preferences?.useCashAccounting ?? true;
                        if (useCashAccounting) {
                            return of({useCashAccounting, accountsPayableLabel: DEFAULT_ACCOUNTS_PAYABLE_LABEL});
                        }

                        return this._chartOfAccountsService.getLiabilities(organizationId).pipe(
                                map((liabilities: Liability[]): AccountingContext => ({
                                    useCashAccounting,
                                    accountsPayableLabel: liabilities
                                            .find(liability => liability.internalUse === Liability.INTERNAL_USE_ACCOUNTS_PAYABLE)
                                            ?.label ?? DEFAULT_ACCOUNTS_PAYABLE_LABEL,
                                })),
                        );
                    }),
                    shareReplay({bufferSize: 1, refCount: false}),
            );
        }

        return this._accountingContext$;
    }

    private clearJournalEntryMatch(): void {
        this.matchedJournalEntry = null;
        this.isLoadingJournalEntry = false;
        this.journalEntryActionItems = [];
        this.currentEntryRows = [];
        this.currentEntryFooter = undefined;
        this.suggestedReplaceBankAccountId = null;
        this.markAsPaidFormGroup.get('journalEntryAction').setValue(null);
        this.refreshJournalEntryView();
        this.emitJournalEntrySummary();
    }

    /**
     * REPLACE posts a payment entry that credits a bank. A journal entry has no bank owner and a
     * payable stores none, so core requires bankAccountForTransaction for REPLACE — the user picks it
     * here. KEEP posts nothing and must not send one.
     */
    private applyJournalEntryActionValidation(action: JournalEntryMatchAction | null): void {
        const bankControl = this.markAsPaidFormGroup.get('bankAccountForTransaction');

        if (action === JournalEntryMatchAction.REPLACE) {
            bankControl.setValidators([Validators.required]);
        } else if (this.markAsPaidFormGroup.get('paymentRecordType').value === PaymentRecordType.MATCH) {
            bankControl.clearValidators();
            bankControl.setValue(null, {emitEvent: false});
        }
        bankControl.updateValueAndValidity({emitEvent: false});
        this.buildJournalEntryPreview();
    }

    private buildJournalEntryPreview(): void {
        if (!this.matchedJournalEntry) {
            return;
        }

        this.journalEntryActionItems = [
            {
                value: JournalEntryMatchAction.KEEP,
                label: 'Keep this journal entry',
                detail: 'Link the existing entry to this payment exactly as it is — your accounts and amounts don\'t change.',
            },
            {
                value: JournalEntryMatchAction.REPLACE,
                label: 'Replace with the standard payment entry',
                detail: `Overwrite it with the entry PayHOA normally records for a payment (${this.standardEntryDescription()}).`,
            },
        ];

        const currentLines = (this.matchedJournalEntry.journalEntries ?? []).map(line => this.toLedgerLine(line));
        this.currentEntryRows = this.toRows(currentLines);
        this.currentEntryFooter = this.toFooter(currentLines);
        this.suggestedReplaceBankAccountId = this.bankAccountCreditedBy(this.matchedJournalEntry);
    }

    get standardEntryRows(): LedgerRowView[] {
        return this.standardEntryView()?.rows ?? [];
    }

    get standardEntryFooter(): LedgerRowView {
        return this.standardEntryView()?.footer;
    }

    /**
     * The REPLACE preview, rebuilt only when what it shows changes. Read from the bank control rather
     * than its valueChanges: the bank picker pre-selects an account without emitting, and the
     * credit row has to show it either way.
     */
    private standardEntryView(): { key: string, rows: LedgerRowView[], footer: LedgerRowView } | null {
        if (!this.matchedJournalEntry) {
            return null;
        }

        const bank = this.markAsPaidFormGroup.get('bankAccountForTransaction').value;
        const key = [
            this.matchedJournalEntry.id,
            bank?.id ?? '',
            bank?.label || bank?.name || '',
            this.accountingContext?.useCashAccounting,
            this.localPayable?.expenseCategories?.length ?? 0,
        ].join('|');

        if (this._standardEntryView?.key !== key) {
            const lines = this.standardEntryLines();
            this._standardEntryView = {key, rows: this.toRows(lines), footer: this.toFooter(lines)};
        }

        return this._standardEntryView;
    }

    /** The bank account a journal entry credits, when it credits exactly one. */
    private bankAccountCreditedBy(entry: Transaction): number | null {
        const bankAccountIds = new Set(
                (entry.journalEntries ?? [])
                        .filter(line => line.type === JournalEntryLineType.CREDIT && line.class instanceof FixedAsset)
                        .map(line => line.class as FixedAsset)
                        .filter(account => account.ownerType === UNIFIED_BANK_ACCOUNT_OWNER_TYPE && !!account.ownerId)
                        .map(account => account.ownerId),
        );

        return bankAccountIds.size === 1 ? [...bankAccountIds][0] : null;
    }

    /** Only the org's own basis is described — never what the other basis would do. */
    private standardEntryDescription(): string {
        return this.accountingContext?.useCashAccounting === false
                ? 'debit Accounts Payable, credit bank'
                : 'debit expense, credit bank';
    }

    /**
     * The entry core posts for a REPLACE: cash debits the payable's expense categories, accrual debits
     * Accounts Payable (relieving the invoice entry); both credit the chosen bank.
     */
    private standardEntryLines(): LedgerLine[] {
        const categories = this.localPayable?.expenseCategories ?? [];
        const total = categories.length > 0
                ? categories.reduce((sum, category) => sum + this.expenseCategoryAmount(category), 0)
                : (this.localPayable?.amount ?? this.matchedJournalEntry?.amount ?? 0);

        const debits: LedgerLine[] = this.accountingContext?.useCashAccounting === false || categories.length === 0
                ? [{account: this.debitAccountLabel(), debit: total, credit: 0}]
                : categories.map(category => ({
                    account: category.expenseCategory?.label ?? '',
                    debit: this.expenseCategoryAmount(category),
                    credit: 0,
                }));

        const bank = this.markAsPaidFormGroup.get('bankAccountForTransaction').value;

        return [
            ...debits,
            {account: bank?.label || bank?.name || 'Selected bank account', debit: 0, credit: total},
        ];
    }

    private debitAccountLabel(): string {
        return this.accountingContext?.useCashAccounting === false
                ? (this.accountingContext.accountsPayableLabel ?? DEFAULT_ACCOUNTS_PAYABLE_LABEL)
                : 'Payable expense';
    }

    private expenseCategoryAmount(category: { amount?: number, expenseCategoryAmount?: number }): number {
        return Number(category.amount ?? category.expenseCategoryAmount ?? 0);
    }

    private isAccountsPayableDebit(line: JournalEntry): boolean {
        return line.type === JournalEntryLineType.DEBIT
                && line.class instanceof Liability
                && line.class.internalUse === Liability.INTERNAL_USE_ACCOUNTS_PAYABLE;
    }

    private toLedgerLine(line: JournalEntry): LedgerLine {
        // Core stores liability, equity and income lines with the sign flipped (JournalEntryService::writeLine);
        // the side comes from `type`, so show the magnitude, as the journal entry editor does.
        const amount = Math.abs(Number(line.amount) || 0);
        const isDebit = line.type === JournalEntryLineType.DEBIT;

        return {
            account: line.class?.label || line.class?.name || '',
            debit: isDebit ? amount : 0,
            credit: isDebit ? 0 : amount,
        };
    }

    trackByLedgerRow(_index: number, row: LedgerRowView): number {
        return row.id;
    }

    private toRows(lines: LedgerLine[]): LedgerRowView[] {
        return lines.map((line, index) => ({
            id: index,
            account: line.account,
            debit: line.debit ? this.formatAmount(line.debit) : EMPTY_AMOUNT,
            credit: line.credit ? this.formatAmount(line.credit) : EMPTY_AMOUNT,
        }));
    }

    private toFooter(lines: LedgerLine[]): LedgerRowView {
        return {
            id: lines.length,
            account: 'Totals',
            debit: this.formatAmount(lines.reduce((sum, line) => sum + line.debit, 0)),
            credit: this.formatAmount(lines.reduce((sum, line) => sum + line.credit, 0)),
        };
    }

    private formatAmount(cents: number): string {
        return this._legfiCurrencyPipe.transform(cents, 'USD', 'symbol', '.2');
    }

    private selectedMatch(): Transaction | undefined {
        const matchedId = this.markAsPaidFormGroup?.get('existingTransactionToMatch').value;
        if (matchedId === null || matchedId === undefined) {
            return undefined;
        }

        return this.potentialTransactionMatches.find(transaction => transaction.id === Number(matchedId));
    }

    private organizationId(): number {
        return this.localPayable?.organizationId ?? LegFiJwtService.read()?.orgId;
    }
}
